/** @file arcSector: the swept angle of a rotate drag as a filled, see-through sector mesh updated in place. */
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial.js';
import { Color3 } from '@babylonjs/core/Maths/math.color.js';
import { Mesh } from '@babylonjs/core/Meshes/mesh.js';
import { VertexData } from '@babylonjs/core/Meshes/mesh.vertexData.js';
import type { Scene } from '@babylonjs/core/scene.js';
import type { GizmoFrame } from './gizmoFrame';
import type { GizmoNodes } from './gizmoNodes';
import { GIZMO_PALETTE } from './gizmoPalette';
import type { GizmoArc } from './viewportCtx';
import { VIEWPORT_GROUPS } from './viewportGroups';
import { VIEWPORT_LAYERS } from './viewportLayers';

/** Fan triangles across the swept angle, its opacity (Unity's 25 % fill) and the most it shows (one full turn). */
const SEGMENTS = 48;
const ALPHA = 0.25;
const MAX_RAD = 2 * Math.PI;
/** Babylon local index of unit-frame axis `i` (0, 1, 2; the y/z swap of `axesLocal`). */
const local = (i: number): number => (i === 0 ? 0 : i === 1 ? 2 : 1);

/** `update(frame)` shows `frame.arc` (or hides the sector when there is none); `mesh` for tests. */
export type ArcSector = { mesh: Mesh; positions: Float32Array; update(f: GizmoFrame | null): void };

/** Writes rim point `i` at ring angle `t` into `pos`: in the plane of axes `k + 1`, `k + 2` (Babylon local), or the screen xy. */
function rim(pos: Float32Array, i: number, axis: number, t: number): void {
  const o = 3 * (i + 1);
  const c = Math.cos(t);
  const s = Math.sin(t);
  if (axis === 3) { pos[o] = c; pos[o + 1] = s; pos[o + 2] = 0; return; }
  pos[o + local(axis)] = 0;
  pos[o + local((axis + 1) % 3)] = c;
  pos[o + local((axis + 2) % 3)] = s;
}

/** Writes the rim from the grabbed angle across the swept one (at most a full turn either way). */
function fan(pos: Float32Array, arc: GizmoArc): void {
  const sweep = Math.max(-MAX_RAD, Math.min(MAX_RAD, (arc.sweepDeg * Math.PI) / 180));
  for (let i = 0; i <= SEGMENTS; i++) rim(pos, i, arc.axis, arc.startRad + (sweep * i) / SEGMENTS);
}

/**
 * One fan mesh (centre + `SEGMENTS + 1` rim points, updatable positions) in the gizmo group, both faces, 25 %
 * alpha in the dragged ring's colour. A ring arc hangs from `arcFrame` placed at the gizmo as the drag found it
 * (so a local-space ring turning with the joint does not drag the start along); the outer ring's from `screen`.
 */
export function arcSector(scene: Scene, nodes: GizmoNodes): ArcSector {
  const mesh = new Mesh('viewport-gizmo-arc-sector', scene);
  const positions = new Float32Array(3 * (SEGMENTS + 2));
  const data = new VertexData();
  data.positions = positions;
  data.indices = Array.from({ length: SEGMENTS }, (_, i) => [0, i + 1, i + 2]).flat();
  data.applyToMesh(mesh, true);
  const material = new StandardMaterial('viewport-gizmo-arc-material', scene);
  Object.assign(material, { disableLighting: true, diffuseColor: Color3.Black(), specularColor: Color3.Black(), backFaceCulling: false, alpha: ALPHA, disableDepthWrite: true });
  Object.assign(mesh, { material, layerMask: VIEWPORT_LAYERS.overlay, renderingGroupId: VIEWPORT_GROUPS.gizmo, isPickable: false, alwaysSelectAsActiveMesh: true });
  mesh.setEnabled(false);
  return {
    mesh, positions,
    update: (f) => {
      const arc = f === null ? null : f.arc;
      mesh.setEnabled(arc !== null);
      if (f === null || arc === null) return;
      mesh.parent = arc.axis === 3 ? nodes.silhouette : nodes.arcFrame;
      if (arc.axis !== 3) nodes.placeArc(arc.model, f.size);
      material.emissiveColor = GIZMO_PALETTE.handle[arc.axis] ?? GIZMO_PALETTE.hover;
      fan(positions, arc);
      mesh.getVertexBuffer('position')?.update(positions);
    },
  };
}
